/**
 * The access token lives in module memory and nowhere else (SEC-1).
 *
 * Not localStorage, not sessionStorage, not a readable cookie. Any XSS on this
 * app can read every one of those, and this token authorises money movement.
 * Holding it in a closure means a script injected into the page cannot simply
 * ask for it, and a browser refresh loses it — which is fine, because the
 * httpOnly refresh cookie mints a new one silently.
 *
 * The refresh token is never here at all: it lives in an httpOnly cookie that
 * JavaScript cannot touch by design.
 */

let accessToken: string | null = null;

/** Notified when the session ends, so the UI can send the partner to login. */
type UnauthorizedListener = () => void;
const listeners = new Set<UnauthorizedListener>();

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const clearAccessToken = (): void => {
  accessToken = null;
};

/** Subscribes to session loss. Returns an unsubscribe function. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Called when the refresh cookie is gone or rejected: the session is over. */
export function notifyUnauthorized(): void {
  clearAccessToken();
  for (const listener of listeners) listener();
}

/** Test-only: drops every subscriber between cases. */
export function resetTokenStore(): void {
  accessToken = null;
  listeners.clear();
}
